import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import Pagination from '../../components/Pagination';
import {
  PlusIcon,
  CaretIcon,
  RefreshIcon,
  EditLineIcon,
  TrashIcon,
  OrgBuildingIcon,
} from '../../assets/icons';

/* ------------------------------------------------------------------ *
 * Resource Directory — listing (sidebar → Resources Directory).
 * Figma 1682-4737. Browse / filter constituency resources (schools,
 * hospitals, religious places…). Demo data (no API yet).
 * ------------------------------------------------------------------ */

interface Resource {
  id: string;
  name: string;
  category: string;
  subCategory: string;
  type: string;
  /** Ownership qualifier shown greyed after the type, e.g. "Government". */
  ownership: string;
  location: string;
}

const RESOURCES: Resource[] = [
  {
    id: 'r1',
    name: 'Government Higher Primary School',
    category: 'Education',
    subCategory: 'Primary School',
    type: 'School',
    ownership: 'Government',
    location: 'Alwar',
  },
  {
    id: 'r2',
    name: 'Alwar Model Public Senior Secondary School',
    category: 'Education',
    subCategory: 'Senior Secondary School',
    type: 'Senior Secondary',
    ownership: 'Government',
    location: 'Alwar',
  },
  {
    id: 'r3',
    name: 'Central Public Girls High School',
    category: 'Education',
    subCategory: 'High School',
    type: 'High School',
    ownership: 'Government',
    location: 'Alwar',
  },
  {
    id: 'r4',
    name: "St. Xavier's Convent Secondary School",
    category: 'Education',
    subCategory: 'Secondary School',
    type: 'Secondary School',
    ownership: 'Private',
    location: 'Alwar',
  },
  {
    id: 'r5',
    name: 'Kendriya Vidyalaya Alwar Cantt',
    category: 'Education',
    subCategory: 'Senior Secondary School',
    type: 'Senior Secondary',
    ownership: 'Autonomous',
    location: 'Alwar',
  },
  {
    id: 'r6',
    name: 'District Primary Model School',
    category: 'Education',
    subCategory: 'Primary School',
    type: 'Primary School',
    ownership: 'Government',
    location: 'Alwar',
  },
];

/** Shared grid template so header + body rows stay aligned. */
const GRID =
  'grid grid-cols-[minmax(230px,1.7fr)_120px_minmax(150px,1.2fr)_minmax(150px,1.2fr)_110px_96px] items-center';

/** Distinct, sorted values of a resource field — powers the filter dropdowns. */
const distinct = (key: keyof Resource) =>
  Array.from(new Set(RESOURCES.map((r) => r[key]))).sort();

function ResourceRow({
  row,
  onEdit,
  onDelete,
}: {
  row: Resource;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={[GRID, 'border-t border-[#f3f4f6]'].join(' ')}>
      {/* Name + icon */}
      <div className="flex items-center gap-3 px-6 py-4">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#dbeafe]/70 text-brand">
          <OrgBuildingIcon className="size-4" />
        </span>
        <p className="truncate text-xs font-bold leading-4 text-[#1e293b]">{row.name}</p>
      </div>

      {/* Category */}
      <div className="px-6 py-4 text-xs font-medium text-[#475569]">{row.category}</div>

      {/* Sub category */}
      <div className="px-6 py-4 text-xs font-medium text-[#475569]">{row.subCategory}</div>

      {/* Type + ownership qualifier */}
      <div className="px-6 py-4 text-xs leading-4">
        <span className="font-medium text-[#1e293b]">{row.type} </span>
        <span className="text-[#94a3b8]">({row.ownership})</span>
      </div>

      {/* Location */}
      <div className="px-6 py-4 text-xs font-medium text-[#475569]">{row.location}</div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-6 py-4">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${row.name}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
        >
          <EditLineIcon className="size-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${row.name}`}
          className="rounded p-1 text-[#6b7280] transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}

export default function ResourceDirectoryPage() {
  const navigate = useNavigate();
  const [resources, setResources] = useState<Resource[]>(RESOURCES);
  const [category, setCategory] = useState<string>();
  const [subCategory, setSubCategory] = useState<string>();
  const [type, setType] = useState<string>();
  const [location, setLocation] = useState<string>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const rows = useMemo(
    () =>
      resources.filter(
        (r) =>
          (!category || r.category === category) &&
          (!subCategory || r.subCategory === subCategory) &&
          (!type || r.type === type) &&
          (!location || r.location === location),
      ),
    [resources, category, subCategory, type, location],
  );

  // Reset to the first page whenever the filtered result set changes.
  useEffect(() => setPage(1), [category, subCategory, type, location]);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  const resetFilters = () => {
    setCategory(undefined);
    setSubCategory(undefined);
    setType(undefined);
    setLocation(undefined);
  };

  const removeResource = (id: string) =>
    setResources((list) => list.filter((r) => r.id !== id));

  const caret = <CaretIcon className="size-3.5 text-[#64748b]" />;

  return (
    <div className="flex flex-col gap-6">
      {/* Header + CTA */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Breadcrumbs items={[{ label: 'Dashboard', to: '/admin' }, { label: 'Resource Directory' }]} />
          <h1 className="pt-1 text-2xl font-semibold leading-8 tracking-[-0.6px] text-ink">
            Resource Directory
          </h1>
          <p className="text-sm leading-5 text-ink-secondary">
            Find and manage resources like schools, hospitals, religious places and more in your
            constituency.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/resource-directory/new')}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-card transition-colors hover:bg-brand-dark"
        >
          <PlusIcon className="size-4" />
          Add Resource
        </button>
      </div>

      {/* Filter toolbar */}
      <div className="rounded-xl border border-[#e5e7eb] bg-white p-[17px] shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <Select
            className="min-w-[200px] flex-1"
            allowClear
            placeholder="Category"
            value={category}
            onChange={setCategory}
            options={distinct('category').map((o) => ({ value: o, label: o }))}
            suffixIcon={caret}
          />
          <Select
            className="min-w-[200px] flex-1"
            allowClear
            placeholder="Sub-Category"
            value={subCategory}
            onChange={setSubCategory}
            options={distinct('subCategory').map((o) => ({ value: o, label: o }))}
            suffixIcon={caret}
          />
          <Select
            className="min-w-[200px] flex-1"
            allowClear
            placeholder="Resource Type"
            value={type}
            onChange={setType}
            options={distinct('type').map((o) => ({ value: o, label: o }))}
            suffixIcon={caret}
          />
          <Select
            className="min-w-[200px] flex-1"
            allowClear
            placeholder="Location"
            value={location}
            onChange={setLocation}
            options={distinct('location').map((o) => ({ value: o, label: o }))}
            suffixIcon={caret}
          />
          <button
            type="button"
            onClick={resetFilters}
            aria-label="Reset filters"
            className="rounded-lg border border-[#e5e7eb] bg-white p-2.5 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
          >
            <RefreshIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* Resources table */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold leading-7 text-ink">Resources</h2>
        <div className="overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-card">
          <div className="overflow-x-auto">
            <div className="min-w-[840px]">
              {/* Header */}
              <div className={[GRID, 'border-b border-[#f3f4f6] bg-[#f9fafb]/50'].join(' ')}>
                {(
                  [
                    ['NAME', 'justify-start'],
                    ['CATEGORY', 'justify-start'],
                    ['SUB CATEGORY', 'justify-start'],
                    ['TYPE', 'justify-start'],
                    ['LOCATION', 'justify-start'],
                    ['ACTIONS', 'justify-end text-right'],
                  ] as const
                ).map(([label, align]) => (
                  <div key={label} className={['flex px-6 py-[14px]', align].join(' ')}>
                    <span className="text-[11px] font-bold uppercase leading-[16.5px] tracking-[0.55px] text-[#6b7280]">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Body */}
              {pageRows.length > 0 ? (
                pageRows.map((row) => (
                  <ResourceRow
                    key={row.id}
                    row={row}
                    onEdit={() => navigate('/admin/resource-directory/new')}
                    onDelete={() => removeResource(row.id)}
                  />
                ))
              ) : (
                <p className="px-6 py-12 text-center text-sm text-ink-secondary">
                  No resources match your filters.
                </p>
              )}
            </div>
          </div>

          {/* Pagination */}
          <Pagination
            rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
            rangeEnd={Math.min(page * pageSize, total)}
            total={total}
            noun="resources"
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(v) => {
              setPageSize(v);
              setPage(1);
            }}
          />
        </div>
      </div>
    </div>
  );
}
